"use client"

import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Loader2,
  X,
  Check,
  ChevronDown,
  AlertTriangle,
  Link as LinkIcon,
} from "lucide-react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as LabelPrimitive from "@radix-ui/react-label"
import * as SwitchPrimitives from "@radix-ui/react-switch"
import * as SelectPrimitive from "@radix-ui/react-select"
import { DATASETS } from "../data/datasets"
import { QUANTUM_DATASET_IDS } from "../data/quantumCsvRegistry"


const Dialog = DialogPrimitive.Root
const DialogPortal = DialogPrimitive.Portal

const DialogOverlay = React.forwardRef(function DialogOverlay(props, ref) {
  const { className, ...rest } = props
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={
        `fixed inset-0 z-50 bg-black/60 backdrop-blur-sm
         data-[state=open]:animate-in data-[state=closed]:animate-out ` + (className || "")
      }
      {...rest}
    />
  )
})

const DialogContent = React.forwardRef(function DialogContent(props, ref) {
  const { className, children, ...rest } = props
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={
          `fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2
           flex max-h-[92vh] flex-col overflow-hidden rounded-xl border bg-white p-6 shadow-xl duration-200
           focus:outline-none
           dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 ` + (className || "")
        }
        {...rest}
      >
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})

const DialogHeader = ({ className, ...rest }) => (
  <div className={`flex flex-col space-y-1.5 text-left ${className || ""}`} {...rest} />
)
const DialogFooter = ({ className, ...rest }) => (
  <div className={`mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end ${className || ""}`} {...rest} />
)

const DialogTitle = React.forwardRef(function DialogTitle(props, ref) {
  const { className, ...rest } = props
  return <DialogPrimitive.Title ref={ref} className={`text-lg font-semibold ${className || ""}`} {...rest} />
})

const Label = React.forwardRef(function Label(props, ref) {
  const { className, ...rest } = props
  return <LabelPrimitive.Root ref={ref} className={`text-sm font-medium ${className || ""}`} {...rest} />
})

const Button = React.forwardRef(function Button(props, ref) {
  const { className, ...rest } = props
  return (
    <button
      ref={ref}
      className={`inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2
                  bg-blue-600 text-white hover:bg-blue-700
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                  dark:bg-blue-600 dark:hover:bg-blue-500 ` + (className || "")}
      {...rest}
    />
  )
})

const GhostButton = React.forwardRef(function GhostButton(props, ref) {
  const { className, ...rest } = props
  return (
    <button
      ref={ref}
      className={`inline-flex items-center justify-center rounded-md text-sm font-medium h-9 px-3
                  border bg-white text-slate-800 hover:bg-slate-50
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                  border-slate-300
                  dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 ` + (className || "")}
      {...rest}
    />
  )
})

const Switch = React.forwardRef(function Switch(props, ref) {
  const { className, ...rest } = props
  return (
    <SwitchPrimitives.Root
      ref={ref}
      className={`peer inline-flex h-6 w-11 items-center rounded-full transition-colors
                  data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-300
                  dark:data-[state=unchecked]:bg-slate-600 ` + (className || "")}
      {...rest}
    >
      <SwitchPrimitives.Thumb className="block h-5 w-5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-5" />
    </SwitchPrimitives.Root>
  )
})

const Select = SelectPrimitive.Root
const SelectTrigger = React.forwardRef(function SelectTrigger(props, ref) {
  const { className, children, ...rest } = props
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={`flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm
                  bg-white text-slate-900 placeholder:text-slate-400
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  border-slate-300
                  dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-400 ` + (className || "")}
      {...rest}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-60" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
})

const SelectContent = React.forwardRef(function SelectContent(props, ref) {
  const { className, children, ...rest } = props
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={`relative z-50 min-w-[12rem] overflow-hidden rounded-md border shadow-md
                    bg-white border-slate-200
                    dark:bg-slate-800 dark:border-slate-700 ` + (className || "")}
        {...rest}
      >
        <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})

const SelectItem = React.forwardRef(function SelectItem(props, ref) {
  const { className, children, ...rest } = props
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={`relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm
                  hover:bg-blue-50 hover:text-blue-900
                  dark:hover:bg-slate-700 dark:hover:text-slate-100 ` + (className || "")}
      {...rest}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
})

const LSH_TABLE_OPTIONS = [15, 20, 25, 35]
const LSH_BUCKET_OPTIONS = [3, 4, 5, 6]
const JACCARD_OPTIONS = [0.25, 0.5, 0.75, 1]
function pickAllowed(value, allowed, fallback) {
  const num = Number(value)
  return allowed.includes(num) ? num : fallback
}

export default function RunConfigWizard({
  open,
  onOpenChange,
  initialRunParams,
  initialConn,
  onSubmit,
}) {
  const [step, setStep] = useState(0)
  const prevOpenRef = useRef(open)

  // reset step when the modal opens fresh
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      setStep(0)
    }
    prevOpenRef.current = open
  }, [open])

  const [conn, setConn] = useState(() => ({
    url: initialConn?.url || "neo4j+s://demo.neo4j.io:7687",
    username: initialConn?.username || "",
    password: initialConn?.password || "",
  }))
  const [connecting, setConnecting] = useState(false)
  const [connected, setConnected] = useState(false)

  const [runParams, setRunParams] = useState(() => ({
    dataset: initialRunParams?.dataset || (DATASETS?.[0]?.id || "POLE"),
    incremental: !!initialRunParams?.incremental,
    incrementalValue: initialRunParams?.incrementalValue ?? 100000,
    knownNodesPercentage: initialRunParams?.knownNodesPercentage ?? 100,
    adaptive: initialRunParams?.adaptive ?? true,
    spectralAutoBalancing: initialRunParams?.spectralAutoBalancing ?? true,
    extensiveEvaluation: !!initialRunParams?.extensiveEvaluation,

    lshVariant: initialRunParams?.lshVariant || "ELSH", // "ELSH" | "MinHash"
    lshHashTables: pickAllowed(
      initialRunParams?.lshHashTables,
      LSH_TABLE_OPTIONS,
      20
    ),
    lshHashBucketLength: pickAllowed(
      initialRunParams?.lshHashBucketLength,
      LSH_BUCKET_OPTIONS,
      4
    ),
    jaccardSimilarityThreshold: pickAllowed(
      initialRunParams?.jaccardSimilarityThreshold,
      JACCARD_OPTIONS,
      0.75
    ),

    // future KMeans
    // kMeansK: initialRunParams?.kMeansK ?? 10,
  }))

  const [quantumFixedUi, setQuantumFixedUi] = useState(() => ({
    fixedK: 10,
    topK: 15,
    maxGroupSize: 200,
    minClusterSize: 10,
    minSimilarity: 0.1,
    balanceLambda: 0.5,
    saIters: 2000,
    saRestarts: 6,
    randomSeed: 42,
  }))

  const [quantumSpectralUi, setQuantumSpectralUi] = useState(() => ({
    balanceTarget: -1,
    balanceLambda: 0.3,
    powerIters: 200,
    tolerance: 0.000001,
    saIters: 2000,
    saRestarts: 6,
    minSimilarity: 0.1,
    minClusterSize: 10,
  }))

  useEffect(() => {
    if (!open || !initialRunParams) return
    setRunParams((prev) => ({
      ...prev,
      ...initialRunParams,
      lshHashTables: pickAllowed(
        initialRunParams?.lshHashTables,
        LSH_TABLE_OPTIONS,
        prev.lshHashTables
      ),
      lshHashBucketLength: pickAllowed(
        initialRunParams?.lshHashBucketLength,
        LSH_BUCKET_OPTIONS,
        prev.lshHashBucketLength
      ),
      jaccardSimilarityThreshold: pickAllowed(
        initialRunParams?.jaccardSimilarityThreshold,
        JACCARD_OPTIONS,
        prev.jaccardSimilarityThreshold
      ),
    }))
    setQuantumFixedUi((prev) => ({
      ...prev,
      fixedK: initialRunParams?.quantumFixedK ?? prev.fixedK,
      topK: initialRunParams?.quantumTopK ?? prev.topK,
      maxGroupSize: initialRunParams?.quantumMaxGroupSize ?? prev.maxGroupSize,
      minClusterSize:
        initialRunParams?.quantumMinClusterSize ?? prev.minClusterSize,
      minSimilarity:
        initialRunParams?.quantumMinSimilarity ?? prev.minSimilarity,
      balanceLambda:
        initialRunParams?.quantumBalanceLambda ?? prev.balanceLambda,
      saIters: initialRunParams?.quantumSaIters ?? prev.saIters,
      saRestarts: initialRunParams?.quantumSaRestarts ?? prev.saRestarts,
      randomSeed: initialRunParams?.quantumRandomSeed ?? prev.randomSeed,
    }))
    setQuantumSpectralUi((prev) => ({
      ...prev,
      balanceTarget:
        initialRunParams?.quantumBalanceTarget ?? prev.balanceTarget,
      balanceLambda:
        initialRunParams?.quantumBalanceLambda ?? prev.balanceLambda,
      powerIters: initialRunParams?.quantumPowerIters ?? prev.powerIters,
      tolerance: initialRunParams?.quantumTolerance ?? prev.tolerance,
      saIters: initialRunParams?.quantumSaIters ?? prev.saIters,
      saRestarts: initialRunParams?.quantumSaRestarts ?? prev.saRestarts,
      minSimilarity:
        initialRunParams?.quantumMinSimilarity ?? prev.minSimilarity,
      minClusterSize:
        initialRunParams?.quantumMinClusterSize ?? prev.minClusterSize,
    }))
  }, [open, initialRunParams])

  const { isValid, errors } = useMemo(() => {
    const errs = []
    if (runParams.incremental && !(runParams.incrementalValue > 0)) {
      errs.push("Incremental value must be > 0.")
    }
    if (!runParams.adaptive) {
      if (!(runParams.lshHashTables > 0)) {
        errs.push("LSH Hash Tables (T) must be > 0.")
      }
      if (runParams.lshVariant === "ELSH") {
        if (!(runParams.lshHashBucketLength > 0)) {
          errs.push("LSH Hash Bucket Length (b) must be > 0 for ELSH.")
        }
        if (!(runParams.jaccardSimilarityThreshold > 0)) {
          errs.push("Jaccard Similarity must be > 0.")
        }
        if (runParams.jaccardSimilarityThreshold > 1) {
          errs.push("Jaccard Similarity cannot be > 1.")
        }
      }
    }
    return { isValid: errs.length === 0, errors: errs }
  }, [runParams])

  const useDemoServer = () => {
    setConn({
      url: "neo4j+s://demo.neo4j.io:7687",
      username: "neo4j",
      password: "demo1234",
    })
  }

  const handleConnect = async () => {
    if (!conn.url || !conn.username || !conn.password) return
    setConnecting(true)
    await new Promise((r) => setTimeout(r, 1200)) // dummy handshake
    setConnecting(false)
    setConnected(true)
    setTimeout(() => setStep(1), 400)
  }

  const [isRunning, setIsRunning] = useState(false)

  const handleRun = async (e) => {
    e.preventDefault()
    if (!isValid || isRunning) return

    setIsRunning(true)
    try {
      const finalRunParams = {
        ...runParams,
        knownTypeLabelPercentage:
          runParams.knownTypeLabelPercentage ?? runParams.knownNodesPercentage,
      }

      if (isQuantumVariant) {
        Object.assign(finalRunParams, {
          quantumBalanceLambda: runParams.spectralAutoBalancing
            ? quantumSpectralUi.balanceLambda
            : quantumFixedUi.balanceLambda,
          quantumSaIters: runParams.spectralAutoBalancing
            ? quantumSpectralUi.saIters
            : quantumFixedUi.saIters,
          quantumSaRestarts: runParams.spectralAutoBalancing
            ? quantumSpectralUi.saRestarts
            : quantumFixedUi.saRestarts,
          quantumMinSimilarity: runParams.spectralAutoBalancing
            ? quantumSpectralUi.minSimilarity
            : quantumFixedUi.minSimilarity,
          quantumMinClusterSize: runParams.spectralAutoBalancing
            ? quantumSpectralUi.minClusterSize
            : quantumFixedUi.minClusterSize,
        })

        if (runParams.spectralAutoBalancing) {
          Object.assign(finalRunParams, {
            quantumBalanceTarget: quantumSpectralUi.balanceTarget,
            quantumPowerIters: quantumSpectralUi.powerIters,
            quantumTolerance: quantumSpectralUi.tolerance,
          })
        }

        if (!runParams.adaptive) {
          Object.assign(finalRunParams, {
            quantumFixedK: quantumFixedUi.fixedK,
            quantumK: quantumFixedUi.fixedK,
            quantumTopK: quantumFixedUi.topK,
            quantumMaxGroupSize: quantumFixedUi.maxGroupSize,
            quantumRandomSeed: quantumFixedUi.randomSeed,
          })
        }
      }

      const payload = { conn, runParams: finalRunParams }
      await Promise.all([
        onSubmit ? Promise.resolve(onSubmit(payload)) : Promise.resolve(),
        new Promise((r) => setTimeout(r, 500)),
      ])
      onOpenChange(false)
    } finally {
      setIsRunning(false)
    }
  }

  const isQuantumVariant =
    runParams.lshVariant === "QUANTUM_FAST" || runParams.lshVariant === "QUANTUM"
  const isLshVariant =
    runParams.lshVariant === "ELSH" || runParams.lshVariant === "MinHash"
  const quantumDatasets = useMemo(
    () =>
      QUANTUM_DATASET_IDS.map((id) => DATASETS.find((ds) => ds.id === id)).filter(Boolean),
    []
  )
  const availableDatasets = isQuantumVariant ? quantumDatasets : DATASETS

  useEffect(() => {
    if (!availableDatasets.length) return
    if (availableDatasets.some((ds) => ds.id === runParams.dataset)) return
    setRunParams((p) => ({ ...p, dataset: availableDatasets[0].id }))
  }, [availableDatasets, runParams.dataset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <div className="mb-4 flex items-center gap-2 text-sm">
          <div
            className={`rounded-md px-2 py-1 border
              ${step === 0
                ? "border-blue-500 bg-blue-600/10 text-blue-700 dark:bg-blue-500/10 dark:text-blue-200"
                : "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
          >
            1. Connect
          </div>
          <div
            className={`rounded-md px-2 py-1 border
              ${step === 1
                ? "border-blue-500 bg-blue-600/10 text-blue-700 dark:bg-blue-500/10 dark:text-blue-200"
                : "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
          >
            2. Parameters
          </div>
        </div>

        {step === 0 ? (
          <>
            <DialogHeader>
              <DialogTitle>Connect to Neo4j</DialogTitle>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Add your connection details or use our demo server.
              </p>
            </DialogHeader>

            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid gap-3">
                <label className="grid gap-1">
                  <span className="text-sm font-medium">Endpoint (Bolt/neo4j+S)</span>
                  <input
                    className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                    placeholder="neo4j+s://host:7687"
                    value={conn.url}
                    onChange={(e) => setConn((p) => ({ ...p, url: e.target.value }))}
                  />
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <label className="grid gap-1">
                    <span className="text-sm font-medium">Username</span>
                    <input
                      className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                                 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      placeholder="neo4j"
                      value={conn.username}
                      onChange={(e) =>
                        setConn((p) => ({ ...p, username: e.target.value }))
                      }
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-sm font-medium">Password</span>
                    <input
                      className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                                 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      type="password"
                      placeholder="••••••••"
                      value={conn.password}
                      onChange={(e) =>
                        setConn((p) => ({ ...p, password: e.target.value }))
                      }
                    />
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <GhostButton type="button" onClick={useDemoServer}>
                    Use demo server
                  </GhostButton>
                  {connected ? (
                    <span className="inline-flex items-center gap-1 text-sm text-emerald-700 dark:text-emerald-300">
                      <Check className="h-4 w-4" />
                      Connected to{" "}
                      <a
                        href={conn.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 underline decoration-slate-400 hover:decoration-blue-500"
                      >
                        <LinkIcon className="h-3 w-3" />
                        {(() => {
                          try {
                            return new URL(conn.url).host
                          } catch {
                            return conn.url
                          }
                        })()}
                      </a>
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <DialogFooter className="pt-4">
              <GhostButton type="button" onClick={() => onOpenChange(false)}>
                Cancel
              </GhostButton>
              <Button
                type="button"
                onClick={handleConnect}
                disabled={connecting || !conn.url || !conn.username || !conn.password}
              >
                {connecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting…
                  </>
                ) : (
                  "Connect"
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleRun} className="flex min-h-0 flex-1 flex-col">
            <DialogHeader>
              <DialogTitle>Parameters</DialogTitle>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                You’re connected to{" "}
                <a
                  href={conn.url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-slate-400 hover:decoration-blue-500"
                >
                  {conn.url}
                </a>
                . The run will use cached demo results.
              </p>
            </DialogHeader>

            {!isValid && errors.length > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-md border p-3 text-sm
                              border-red-200 bg-red-50 text-red-800
                              dark:border-red-900 dark:bg-red-950 dark:text-red-200">
                <AlertTriangle className="mt-0.5 h-4 w-4" />
                <div>{errors.map((e, i) => <div key={i}>• {e}</div>)}</div>
              </div>
            )}

            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid gap-6 pb-1">
                <div className="flex items-center gap-4">
                  <Switch
                    id="incremental"
                    checked={runParams.incremental}
                    onCheckedChange={(checked) =>
                      setRunParams((p) => ({ ...p, incremental: !!checked }))
                    }
                  />
                  <Label htmlFor="incremental" className="text-base">
                    Run incrementally
                  </Label>
                </div>

                {runParams.incremental && (
                  <div className="grid grid-cols-[200px_1fr] items-center gap-4">
                    <Label htmlFor="incrementalValue" className="text-right text-base">
                      Incremental value
                    </Label>
                    <Select
                      value={String(runParams.incrementalValue ?? "")}
                      onValueChange={(v) =>
                        setRunParams((p) => ({
                          ...p,
                          incrementalValue: parseInt(v, 10),
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectPrimitive.Value placeholder="Select incremental value" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100000">100.000</SelectItem>
                        <SelectItem value="500000">500.000</SelectItem>
                        <SelectItem value="1000000">1.000.000</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

              {/* <div className="grid grid-cols-[200px_1fr] items-center gap-4">
                <Label htmlFor="knownNodesPercentage" className="text-right text-base">
                  Known Type Labels Percentage
                </Label>
                <Select
                  value={String(runParams.knownNodesPercentage ?? "")}
                  onValueChange={(v) =>
                    setRunParams((p) => ({
                      ...p,
                      knownNodesPercentage: parseInt(v, 10),
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectPrimitive.Value placeholder="Select percentage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="100">100%</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              <div className="grid grid-cols-[200px_1fr] items-center gap-4">
                <Label htmlFor="dataset" className="text-right text-base">
                  Dataset
                </Label>
                <Select
                  value={runParams.dataset}
                  onValueChange={(v) => setRunParams((p) => ({ ...p, dataset: v }))}
                >
                  <SelectTrigger>
                    <SelectPrimitive.Value placeholder="Select dataset" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDatasets.map((ds) => (
                      <SelectItem key={ds.id} value={ds.id}>
                        {ds.label} ({ds.nodes.toLocaleString()} N, {ds.edges.toLocaleString()} E)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-[200px_1fr] items-start gap-4">
                <Label className="text-right text-base">
                  Clustering method
                </Label>
                <div className="space-y-2">
                  <Select
                    value={runParams.lshVariant}
                    onValueChange={(v) =>
                      setRunParams((p) => ({ ...p, lshVariant: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectPrimitive.Value placeholder="Select LSH variant" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ELSH">ELSH</SelectItem>
                      <SelectItem value="MinHash">MinHash LSH</SelectItem>
                      <SelectItem value="QUANTUM_FAST">QUANTUM(fast)</SelectItem>
                      <SelectItem value="QUANTUM">Quantum</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 opacity-70">
                    <span>KMeans (coming soon):</span>
                    <input
                      type="number"
                      readOnly
                      className="h-8 w-16 rounded-md border px-2 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-xs"
                      value={runParams.kMeansK}
                    />
                    <span>K (disabled)</span>
                  </div> */}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Switch
                  id="adaptive"
                  checked={runParams.adaptive}
                  onCheckedChange={(checked) =>
                    setRunParams((p) => ({ ...p, adaptive: !!checked }))
                  }
                />
                <Label htmlFor="adaptive" className="text-base">
                  {isQuantumVariant
                    ? "Adaptive clustering (Auto-K)"
                    : "Adaptive clustering (auto-tune)"}
                </Label>
              </div>

              {isQuantumVariant && (
                <div className="flex items-center gap-4">
                  <Switch
                    id="spectralAutoBalancing"
                    checked={!!runParams.spectralAutoBalancing}
                    onCheckedChange={(checked) =>
                      setRunParams((p) => ({ ...p, spectralAutoBalancing: !!checked }))
                    }
                  />
                  <Label htmlFor="spectralAutoBalancing" className="text-base">
                    Spectral Auto Balancing
                  </Label>
                </div>
              )}

              {isQuantumVariant && !runParams.spectralAutoBalancing && (
                <div className="grid gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_BALANCE_TARGET</span>
                      <input
                        type="number"
                        step={0.01}
                        value={quantumSpectralUi.balanceTarget}
                        onChange={(e) =>
                          setQuantumSpectralUi((p) => ({
                            ...p,
                            balanceTarget: Number(e.target.value) || 0,
                          }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_BALANCE_LAMBDA</span>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={quantumSpectralUi.balanceLambda}
                        onChange={(e) =>
                          setQuantumSpectralUi((p) => ({
                            ...p,
                            balanceLambda: Number(e.target.value) || 0,
                          }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>Spectral Power Iters</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumSpectralUi.powerIters}
                        onChange={(e) =>
                          setQuantumSpectralUi((p) => ({
                            ...p,
                            powerIters: Number(e.target.value) || 0,
                          }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>Spectral Tolerance</span>
                      <input
                        type="number"
                        min={0}
                        step={0.000001}
                        value={quantumSpectralUi.tolerance}
                        onChange={(e) =>
                          setQuantumSpectralUi((p) => ({
                            ...p,
                            tolerance: Number(e.target.value) || 0,
                          }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_SA_ITERS</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumSpectralUi.saIters}
                        onChange={(e) =>
                          setQuantumSpectralUi((p) => ({
                            ...p,
                            saIters: Number(e.target.value) || 0,
                          }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_SA_RESTARTS</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumSpectralUi.saRestarts}
                        onChange={(e) =>
                          setQuantumSpectralUi((p) => ({
                            ...p,
                            saRestarts: Number(e.target.value) || 0,
                          }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_MIN_SIMILARITY</span>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={quantumSpectralUi.minSimilarity}
                        onChange={(e) =>
                          setQuantumSpectralUi((p) => ({
                            ...p,
                            minSimilarity: Number(e.target.value) || 0,
                          }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_MIN_CLUSTER_SIZE</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumSpectralUi.minClusterSize}
                        onChange={(e) =>
                          setQuantumSpectralUi((p) => ({
                            ...p,
                            minClusterSize: Number(e.target.value) || 0,
                          }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                  </div>
                </div>
              )}

              {isQuantumVariant && !runParams.adaptive && (
                <div className="grid gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_FIXED_K</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumFixedUi.fixedK}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, fixedK: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_TOP_K / topK</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumFixedUi.topK}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, topK: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>maxGroupSize</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumFixedUi.maxGroupSize}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, maxGroupSize: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_MIN_CLUSTER_SIZE</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumFixedUi.minClusterSize}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, minClusterSize: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_MIN_SIMILARITY</span>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={quantumFixedUi.minSimilarity}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, minSimilarity: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_BALANCE_LAMBDA</span>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={quantumFixedUi.balanceLambda}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, balanceLambda: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_SA_ITERS</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumFixedUi.saIters}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, saIters: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_SA_RESTARTS</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={quantumFixedUi.saRestarts}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, saRestarts: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>QUANTUM_RANDOM_SEED</span>
                      <input
                        type="number"
                        step={1}
                        value={quantumFixedUi.randomSeed}
                        onChange={(e) =>
                          setQuantumFixedUi((p) => ({ ...p, randomSeed: Number(e.target.value) || 0 }))
                        }
                        className="h-10 rounded-md border px-3 bg-white text-slate-900 placeholder:text-slate-400
                               focus:outline-none focus:ring-2 focus:ring-blue-500 border-slate-300
                               dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400 dark:border-slate-700"
                      />
                    </label>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="extensiveEvaluation"
                  checked={runParams.extensiveEvaluation}
                  onChange={(e) =>
                    setRunParams((p) => ({ ...p, extensiveEvaluation: e.target.checked }))
                  }
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800"
                />
                <Label htmlFor="extensiveEvaluation" className="text-base cursor-pointer">
                  Extensive evaluation
                </Label>
              </div>

              {isLshVariant && !runParams.adaptive && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="grid gap-1 text-sm">
                    <span>LSH Hash Tables (T)</span>
                    <Select
                      value={String(runParams.lshHashTables)}
                      onValueChange={(v) =>
                        setRunParams((p) => ({
                          ...p,
                          lshHashTables: Number(v),
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectPrimitive.Value placeholder="Select T" />
                      </SelectTrigger>
                      <SelectContent>
                        {LSH_TABLE_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={String(opt)}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  {runParams.lshVariant === "ELSH" && (
                    <label className="grid gap-1 text-sm">
                      <span>LSH Hash Bucket Length (b)</span>
                      <Select
                        value={String(runParams.lshHashBucketLength)}
                        onValueChange={(v) =>
                          setRunParams((p) => ({
                            ...p,
                            lshHashBucketLength: Number(v),
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectPrimitive.Value placeholder="Select b" />
                        </SelectTrigger>
                        <SelectContent>
                          {LSH_BUCKET_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={String(opt)}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </label>
                  )}
                  <label className="grid gap-1 text-sm">
                    <span>Jaccard Similarity =</span>
                    <Select
                      value={String(runParams.jaccardSimilarityThreshold)}
                      onValueChange={(v) =>
                        setRunParams((p) => ({
                          ...p,
                          jaccardSimilarityThreshold: Number(v),
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectPrimitive.Value placeholder="Select Jaccard" />
                      </SelectTrigger>
                      <SelectContent>
                        {JACCARD_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={String(opt)}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                </div>
              )}
              </div>
            </div>

            <DialogFooter className="pt-4">
              <GhostButton type="button" onClick={() => setStep(0)}>
                Back
              </GhostButton>
              <Button type="submit" disabled={!isValid || isRunning}>
                {isRunning ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Running…
                  </>
                ) : (
                  "Run"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
